/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { PenSquare, Image as ImageIcon, X, Youtube } from 'lucide-react';
import { fileToBase64 } from '@/lib/utils-cat';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';

const PostComposer = ({ onCreated, triggerButton }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  const [images, setImages] = useState([]);
  const [youtube, setYoutube] = useState('');
  const [audience, setAudience] = useState('all'); // 'all' | 'mine'
  const [submitting, setSubmitting] = useState(false);

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files || []);
    const arr = [];
    for (const f of files.slice(0, 4)) {
      try {
        const b64 = await fileToBase64(f);
        arr.push(b64);
      } catch {}
    }
    setImages((prev) => [...prev, ...arr].slice(0, 4));
    e.target.value = '';
  };

  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  const reset = () => {
    setContent(''); setImages([]); setYoutube(''); setAudience('all');
  };

  const submit = async () => {
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      const payload = {
        content: content.trim(),
        images_base64: images,
        youtube_url: youtube.trim() || null,
        audience_category: audience === 'mine' ? user?.category : null,
      };
      const res = await api.post('/posts', payload);
      toast.success(t('postCreated'));
      setOpen(false);
      reset();
      onCreated && onCreated(res.data);
    } catch (e) {
      toast.error(t('errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        {triggerButton || (
          <Button className="gap-2" data-testid="post-composer-open-button">
            <PenSquare className="h-4 w-4" />
            {t('newPost')}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[560px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('newPost')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Textarea
            data-testid="post-composer-textarea"
            placeholder={t('shareIdea')}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={5}
            className="resize-none"
          />

          {images.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {images.map((img, idx) => (
                <div key={img.slice(0, 32) + idx} className="relative aspect-video rounded-lg overflow-hidden bg-secondary">
                  <img src={img} alt={`preview-${idx}`} className="w-full h-full object-cover" />
                  <button
                    onClick={() => removeImage(idx)}
                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center"
                    data-testid={`post-composer-remove-image-${idx}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-secondary hover:bg-secondary/80 cursor-pointer transition" data-testid="post-composer-image-label">
              <ImageIcon className="h-4 w-4" />
              {t('addImages')}
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden-file hidden"
                onChange={handleImageChange}
                data-testid="post-composer-image-input"
              />
            </label>
          </div>

          <div className="flex items-center gap-2 border border-input rounded-xl px-3">
            <Youtube className="h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="post-composer-youtube-input"
              placeholder={t('youtubeLink')}
              value={youtube}
              onChange={(e) => setYoutube(e.target.value)}
              className="border-0 focus-visible:ring-0 shadow-none px-0"
            />
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">{t('audience')}</Label>
            <RadioGroup value={audience} onValueChange={setAudience} className="mt-2 flex gap-4" data-testid="post-composer-audience">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="all" id="aud-all" data-testid="post-composer-audience-all" />
                <Label htmlFor="aud-all" className="text-sm cursor-pointer">{t('audienceAll')}</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="mine" id="aud-mine" data-testid="post-composer-audience-mine" />
                <Label htmlFor="aud-mine" className="text-sm cursor-pointer">{t('audienceSpecific')}</Label>
              </div>
            </RadioGroup>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="post-composer-cancel-button">{t('cancel')}</Button>
          <Button onClick={submit} disabled={submitting || !content.trim()} data-testid="post-composer-submit-button">
            {submitting ? t('loading') : t('publish')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PostComposer;
